import React, { useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { components as ReactSelectComponents, OptionProps } from "react-select";

import CompactSelect from "@vahak/core-ui/dist/components/CompactSelect";
import Checkbox from "@vahak/core-ui/dist/components/Checkbox";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";
import { getAllowedVehicleList } from "@vahak/core/dist/constants/lorryTypes";
import LorryOutline from "@vahak/core/dist/standard-icons/lorry-outline.svg";
import DownArrowIcon from "@vahak/core/dist/standard-icons/down_arrow.svg";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";

import { generateSelectOption } from "../../../../../methods/uiUtil";
import FieldShellStyles from "../MarketplaceLoadFilterCard.module.scss";
import {
    createQuoteFormChevronIndicator,
    createQuoteFormLorryControl,
    createQuoteFormLocationPlaceholder,
    createQuoteFormMultiValueContainer,
    hiddenMultiValue
} from "./marketplaceQuoteFormFieldParts";
import MarketplaceFilterMobileSheet from "./MarketplaceFilterMobileSheet";

interface MarketplaceFilterLorryTypeFieldProps {
    id?: string;
    placeholder: string;
    value?: number[];
    mobileTitle: string;
    onChange: (lorryTypeIds: number[]) => void;
}

const MarketplaceFilterLorryTypeField = ({
    id = "marketplace-filter-lorry-type",
    placeholder,
    value = [],
    mobileTitle,
    onChange
}: MarketplaceFilterLorryTypeFieldProps) => {
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const [mobileOpen, setMobileOpen] = useState(false);
    const selectRef = useRef<any>(null);
    const baseLorryList = getAllowedVehicleList("lorry");
    const resolvedLorryList = baseLorryList.length ? baseLorryList : getAllowedVehicleList("load");
    const lorryOptions = resolvedLorryList.map((lorryType) =>
        generateSelectOption(lorryType.label, lorryType.id, lorryType.id)
    );

    const selectedOptions = lorryOptions.filter((option) => value.includes(option.value));
    const displayLabel = selectedOptions.length ? selectedOptions.map((option) => option.label).join(", ") : "";

    const handleChange = (selected: SelectOption<number>[] | null) => {
        const selectedArr = Array.isArray(selected) ? selected : [];
        onChange(selectedArr.map((option) => option.value));
    };

    const selectComponents = useMemo(
        () => ({
            Control: createQuoteFormLorryControl(
                FieldShellStyles,
                <LorryOutline className={FieldShellStyles.fieldIcon} />
            ),
            DropdownIndicator: createQuoteFormChevronIndicator(FieldShellStyles, <DownArrowIcon aria-hidden />),
            Placeholder: createQuoteFormLocationPlaceholder(FieldShellStyles),
            Menu: ({ className, ...props }: any) => (
                <ReactSelectComponents.Menu
                    {...props}
                    className={classnames(className, FieldShellStyles.lorryDropdownMenu)}
                />
            ),
            Option: (props: OptionProps<SelectOption<number>, true>) => (
                <ReactSelectComponents.Option
                    {...props}
                    className={classnames(
                        FieldShellStyles.lorryOption,
                        props.isFocused && FieldShellStyles.lorryOptionActive,
                        props.isSelected && FieldShellStyles.lorryOptionSelected
                    )}
                >
                    <div className={FieldShellStyles.checkboxOptionRow}>
                        <span className={FieldShellStyles.locationOptionText}>{props.label}</span>
                        <Checkbox
                            id={`marketplace-lorry-option-${props.data.value}`}
                            name={`marketplace-lorry-option-${props.data.value}`}
                            label=""
                            value={props.isSelected}
                            onChange={() => null}
                        />
                    </div>
                </ReactSelectComponents.Option>
            ),
            ValueContainer: createQuoteFormMultiValueContainer(FieldShellStyles, displayLabel),
            MultiValue: hiddenMultiValue
        }),
        [displayLabel]
    );

    return (
        <div
            className={classnames(
                FieldShellStyles.fieldWrap,
                FieldShellStyles.fieldWrapSelect,
                FieldShellStyles.fieldWrapMulti
            )}
        >
            <CompactSelect
                ref={selectRef}
                id={id}
                name="lorryType"
                isMulti
                closeMenuOnSelect
                hideSelectedOptions={false}
                option={lorryOptions}
                value={selectedOptions}
                placeholder={placeholder}
                isSearchable={false}
                openMenuOnClick
                menuPosition="absolute"
                onChange={handleChange as any}
                components={selectComponents as any}
                styles={{ multiValue: () => ({ display: "none" }) }}
                {...(isMobileScreen && {
                    menuIsOpen: false,
                    onMenuOpen: () => {
                        selectRef.current?.blur?.();
                        setMobileOpen(true);
                    }
                })}
            />

            {mobileOpen ? (
                <MarketplaceFilterMobileSheet
                    title={mobileTitle}
                    options={lorryOptions}
                    value={selectedOptions}
                    isMulti
                    onChange={(next) => onChange(next.map((option) => option.value))}
                    onClose={() => setMobileOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default MarketplaceFilterLorryTypeField;
